import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IconButton, Option, Select, Typography } from "@mui/joy";
import { Box, Divider, Hidden, Tooltip } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import nerdUII18n from "../i18n";
const itemsPerPage = [25, 50, 75, 100];
/**
 *
 * @param {NerdPaginationFooterProps} props component props
 * @returns {ReactElement | null} PaginationFooter page
 */
export function NerdPaginationFooter({ onSearch, count, loading, defaultItemsPerPage = 25, }) {
    const { t } = useTranslation("components", { i18n: nerdUII18n });
    const [searchParams, setSearchParams] = useSearchParams();
    const offsetString = searchParams.get("offset");
    const limitString = searchParams.get("limit");
    const offset = offsetString ? parseInt(offsetString) : 0;
    const limit = limitString ? parseInt(limitString) : defaultItemsPerPage;
    const apiRef = useGridApiContext();
    const currentPage = offset / limit;
    const [maxPage, setMaxPage] = useState(currentPage + 1);
    const currentPageButton = useRef(null);
    useEffect(() => {
        if (offset % limit !== 0) {
            setSearchParams({ offset: "0" });
        }
    }, []);
    useEffect(() => {
        if (currentPageButton) {
            currentPageButton.current?.scrollIntoView();
        }
    }, [currentPage]);
    const onOffsetChange = (o) => {
        if (o != undefined) {
            setSearchParams((prevState) => {
                prevState.set("offset", `${o}`);
                return prevState;
            });
            onSearch?.({
                filters: searchParams.get("filters"),
                offset: searchParams.get("offset") ?? 0,
                limit: searchParams.get("limit") ?? defaultItemsPerPage,
            });
        }
    };
    const onLimitChange = (l) => {
        if (l != undefined) {
            setSearchParams((prevState) => {
                prevState.set("limit", `${l}`);
                prevState.set("offset", "0");
                return prevState;
            });
            setMaxPage(1);
            if (onSearch)
                onSearch({
                    filters: searchParams.get("filters"),
                    offset: 0,
                    limit: searchParams.get("limit") ?? defaultItemsPerPage,
                });
        }
    };
    const nextPage = () => {
        onOffsetChange((currentPage + 1) * limit);
        if (currentPage + 1 === maxPage)
            setMaxPage(maxPage + 1);
        apiRef.current?.scrollToIndexes({ rowIndex: 0 });
    };
    const previousPage = () => {
        onOffsetChange((currentPage - 1) * limit);
        apiRef.current?.scrollToIndexes({ rowIndex: 0 });
    };
    const goToPage = (i) => {
        onOffsetChange(i * limit);
        apiRef.current?.scrollToIndexes({ rowIndex: 0 });
    };
    if (offset % limit !== 0) {
        return null;
    }
    return (_jsxs(Box, { children: [_jsx(Divider, {}), _jsxs(Box, { sx: {
                    display: "flex",
                    p: 1,
                    justifyContent: "space-between",
                    alignItems: "center",
                }, children: [_jsx(Hidden, { smDown: true, children: _jsx(Select, { startDecorator: _jsx(Typography, { level: "body2", sx: { fontWeight: 500 }, children: "Items per page" }), placeholder: t("nerdPaginationFooter.items_per_page"), value: limit, size: "sm", onChange: (_e, v) => onLimitChange(v), children: itemsPerPage.map((i) => (_jsx(Option, { value: i, children: i }, i))) }) }), _jsxs(Typography, { level: "body2", children: [apiRef.current?.getRowsCount(), " ", t("nerdPaginationFooter.items")] }), _jsxs(Box, { sx: { display: "flex", gap: 1, maxWidth: { lg: 400, xs: 200 } }, children: [_jsx(Tooltip, { title: t("nerdPaginationFooter.previous_page"), children: _jsx("span", { style: { position: "sticky", left: 0, zIndex: 99 }, children: _jsx(IconButton, { disabled: offset === 0 || loading, color: offset === 0 ? "neutral" : "primary", size: "sm", onClick: previousPage, children: _jsx("i", { className: "fa-solid fa-chevron-left" }) }) }) }), _jsx(Box, { sx: {
                                    display: "flex",
                                    gap: 1,
                                    overflow: "scroll",
                                }, children: [...Array(maxPage).keys()].map((i) => (_jsx(IconButton, { size: "sm", ref: currentPage === i ? currentPageButton : null, onClick: () => goToPage(i), color: currentPage === i ? "primary" : "neutral", variant: currentPage === i ? "solid" : "soft", disabled: loading, children: i + 1 }, i))) }), _jsx(Tooltip, { title: t("nerdPaginationFooter.next_page"), children: _jsx("span", { style: { position: "sticky", right: 0, zIndex: 99 }, children: _jsx(IconButton, { disabled: count !== limit || loading, color: count !== limit ? "neutral" : "primary", size: "sm", onClick: nextPage, children: _jsx("i", { className: "fa-solid fa-chevron-right" }) }) }) })] })] })] }));
}
